import { CascaderOption } from 'types/base';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import LOCALS from 'commons/locals';
import birkinFrontImg from './images/birkin/front.webp';
import birkinLogoHandleImg from './images/birkin/logo-handle.webp';
import birkinFishMouthImg from './images/birkin/fish-mouth.webp';
import birkinEdgeOilImg from './images/birkin/edge-oil.webp';
import birkinFullStampImg from './images/birkin/full-stamp.webp';
import birkinPleatJointImg from './images/birkin/pleat-joint.webp';
import birkinStrapBuckleImg from './images/birkin/strap-buckle.webp';
import birkinStrapStampImg from './images/birkin/strap-stamp.webp';
import birkinZipperImg from './images/birkin/zipper.webp';
import birkinZipperIEndImg from './images/birkin/zipper-end.webp';
import birkinLockBottomImg from './images/birkin/lock-bottom.webp';
import birkinKeyImg from './images/birkin/key.webp';
import birkinBottomStudsImg from './images/birkin/bottom-studs.webp';
import kellyFrontImg from './images/kelly/front.webp';
import kellyHandleImg from './images/kelly/handle.webp';
import kellyFishMouthImg from './images/kelly/fish-mouth.webp';
import kellyFullStampImg from './images/kelly/full-stamp.webp';
import kellyLogoImg from './images/kelly/logo.webp';
import kellyPleatJointImg from './images/kelly/pleat-joint.webp';
import kellyStrapBuckleImg from './images/kelly/strap-buckle.webp';
import kellyBottomStudsImg from './images/kelly/bottom-studs.webp';
import kellyHandleStitchingImg from './images/kelly/handle-stitching.webp';
import kellyStrapHardwareEngravingImg from './images/kelly/strap-hardware-engraving.webp';
import constanceFrontImg from './images/constance/front.webp';
import constanceInnerLeatherHardwareImg from './images/constance/inner-leather-hardware.webp';
import constanceHBuckleImg from './images/constance/h-buckle.webp';
import constanceLogoImg from './images/constance/logo.webp';
import constanceEdgeOilWaveImg from './images/constance/edge-oil-wave.webp';
import constanceEdgeOilSideImg from './images/constance/edge-oil-side.webp';
import constanceMetalStampImg from './images/constance/metal-stamp.webp';
import constanceFullStampImg from './images/constance/full-stamp.webp';
import constanceStrapStitchJoinImg from './images/constance/strap-stitch-join.webp';
import constanceMagneticBuckleImg from './images/constance/magnetic-buckle.webp';
import constanceMagneticGrooveImg from './images/constance/magnetic-groove.webp';

// 拍照区域配置类型
export type PhotoZoneConfig = {
  id: string;
  labelKey: string;
  required: boolean;
  backgroundImage?: string;
};

// 区域图片状态类型
export type ZoneImageState = {
  [zoneId: string]: {
    imageId?: number;
    publicUrl?: string;
    backendPath?: string;
    uploading?: boolean;
    file?: File;
    localPreviewUrl?: string;
    originalName?: string;
    type?: string;
  };
};

// 鉴定状态类型
export type AuthenticationStatus =
  | 'idle'
  | 'processing'
  | 'completed'
  | 'failed';

// 鉴定结果类型
export type AuthenticationResult = {
  verdict?: string;
  score?: number;
  mode?: 'normal' | 'insufficient' | 'few-sample-positive';
  scoreKind?: 'numeric' | 'insufficient' | 'none';
  verdictKind?:
    | 'service'
    | 'insufficient'
    | 'few-sample-positive'
    | 'uncertain'
    | 'genuine'
    | 'suspected-fake'
    | 'none';
};

// AI 鉴定图片支持的 MIME 类型（需与上传组件保持一致）
export const AI_IDENTIFY_ALLOWED_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/tiff',
  'image/tif',
] as string[];

export const AI_IDENTIFY_ACCEPT = AI_IDENTIFY_ALLOWED_FORMATS.join(', ');

export const MIN_EFFECTIVE_DETECTION_POINTS = 4;

export const hasInsufficientDetectionPoints = (
  effectiveDetectionPointCount: number,
) => effectiveDetectionPointCount < MIN_EFFECTIVE_DETECTION_POINTS;

// 标准化包型名称
export const normalizeBagType = (bagType?: string) => {
  if (!bagType) return undefined;
  const trimmed = bagType.trim();
  if (!trimmed) return undefined;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

// 支持的包型列表
export const SUPPORTED_BAG_TYPES = ['Birkin', 'Kelly', 'Constance'] as const;

// 各包型的拍照区域配置
export const BAG_TYPE_CONFIG: Record<string, PhotoZoneConfig[]> = {
  Birkin: [
    {
      id: 'front',
      labelKey: LOCALS.ai_identify_zone_front,
      required: true,
      backgroundImage: birkinFrontImg,
    },
    {
      id: 'logo-handle',
      labelKey: LOCALS.ai_identify_zone_logo_handle,
      required: true,
      backgroundImage: birkinLogoHandleImg,
    },
    {
      id: 'fish-mouth',
      labelKey: LOCALS.ai_identify_zone_fish_mouth,
      required: true,
      backgroundImage: birkinFishMouthImg,
    },
    {
      id: 'edge-oil',
      labelKey: LOCALS.ai_identify_zone_edge_oil_top,
      required: true,
      backgroundImage: birkinEdgeOilImg,
    },
    {
      id: 'bottom-studs',
      labelKey: LOCALS.ai_identify_zone_bottom_studs,
      required: true,
      backgroundImage: birkinBottomStudsImg,
    },
    {
      id: 'pleat-joint',
      labelKey: LOCALS.ai_identify_zone_pleat_joint,
      required: true,
      backgroundImage: birkinPleatJointImg,
    },
    {
      id: 'strap-buckle',
      labelKey: LOCALS.ai_identify_zone_strap_buckle,
      required: true,
      backgroundImage: birkinStrapBuckleImg,
    },
    {
      id: 'strap-stamp',
      labelKey: LOCALS.ai_identify_zone_strap_stamp,
      required: false,
      backgroundImage: birkinStrapStampImg,
    },
    {
      id: 'full-stamp',
      labelKey: LOCALS.ai_identify_zone_full_stamp,
      required: false,
      backgroundImage: birkinFullStampImg,
    },
    {
      id: 'zipper',
      labelKey: LOCALS.ai_identify_zone_zipper,
      required: false,
      backgroundImage: birkinZipperImg,
    },
    {
      id: 'zipper-end',
      labelKey: LOCALS.ai_identify_zone_zipper_end,
      required: false,
      backgroundImage: birkinZipperIEndImg,
    },
    {
      id: 'lock-bottom',
      labelKey: LOCALS.ai_identify_zone_lock_bottom,
      required: false,
      backgroundImage: birkinLockBottomImg,
    },
    {
      id: 'key',
      labelKey: LOCALS.ai_identify_zone_key,
      required: false,
      backgroundImage: birkinKeyImg,
    },
  ],
  Kelly: [
    {
      id: 'front',
      labelKey: LOCALS.ai_identify_zone_front,
      required: true,
      backgroundImage: kellyFrontImg,
    },
    {
      id: 'handle',
      labelKey: LOCALS.ai_identify_zone_logo_handle,
      required: true,
      backgroundImage: kellyHandleImg,
    },
    {
      id: 'logo',
      labelKey: LOCALS.ai_identify_zone_logo,
      required: true,
      backgroundImage: kellyLogoImg,
    },
    {
      id: 'fish-mouth',
      labelKey: LOCALS.ai_identify_zone_fish_mouth,
      required: true,
      backgroundImage: kellyFishMouthImg,
    },
    {
      id: 'pleat-joint',
      labelKey: LOCALS.ai_identify_zone_pleat_joint,
      required: true,
      backgroundImage: kellyPleatJointImg,
    },
    {
      id: 'strap-buckle',
      labelKey: LOCALS.ai_identify_zone_strap_buckle,
      required: true,
      backgroundImage: kellyStrapBuckleImg,
    },
    {
      id: 'full-stamp',
      labelKey: LOCALS.ai_identify_zone_full_stamp,
      required: false,
      backgroundImage: kellyFullStampImg,
    },
    {
      id: 'strap-stamp',
      labelKey: LOCALS.ai_identify_zone_strap_stamp,
      required: false,
      backgroundImage: birkinStrapStampImg,
    },
    {
      id: 'handle-stitching',
      labelKey: LOCALS.ai_identify_zone_handle_stitching,
      required: false,
      backgroundImage: kellyHandleStitchingImg,
    },
    {
      id: 'bottom-studs',
      labelKey: LOCALS.ai_identify_zone_bottom_studs,
      required: false,
      backgroundImage: kellyBottomStudsImg,
    },
    {
      id: 'strap-hardware-engraving',
      labelKey: LOCALS.ai_identify_zone_strap_hardware_engraving,
      required: false,
      backgroundImage: kellyStrapHardwareEngravingImg,
    },
    {
      id: 'zipper',
      labelKey: LOCALS.ai_identify_zone_zipper,
      required: false,
      backgroundImage: birkinZipperImg,
    },
    {
      id: 'zipper-end',
      labelKey: LOCALS.ai_identify_zone_zipper_end,
      required: false,
      backgroundImage: birkinZipperIEndImg,
    },
    {
      id: 'lock-bottom',
      labelKey: LOCALS.ai_identify_zone_lock_bottom,
      required: false,
      backgroundImage: birkinLockBottomImg,
    },
    {
      id: 'key',
      labelKey: LOCALS.ai_identify_zone_key,
      required: false,
      backgroundImage: birkinKeyImg,
    },
  ],
  Constance: [
    {
      id: 'front',
      labelKey: LOCALS.ai_identify_zone_front,
      required: true,
      backgroundImage: constanceFrontImg,
    },
    {
      id: 'inner-leather-hardware',
      labelKey: LOCALS.ai_identify_zone_inner_leather_hardware,
      required: true,
      backgroundImage: constanceInnerLeatherHardwareImg,
    },
    {
      id: 'h-buckle',
      labelKey: LOCALS.ai_identify_zone_h_buckle,
      required: true,
      backgroundImage: constanceHBuckleImg,
    },
    {
      id: 'logo',
      labelKey: LOCALS.ai_identify_zone_logo,
      required: true,
      backgroundImage: constanceLogoImg,
    },
    {
      id: 'edge-oil-wave',
      labelKey: LOCALS.ai_identify_zone_edge_oil_wave,
      required: true,
      backgroundImage: constanceEdgeOilWaveImg,
    },
    {
      id: 'edge-oil-side',
      labelKey: LOCALS.ai_identify_zone_edge_oil_side,
      required: true,
      backgroundImage: constanceEdgeOilSideImg,
    },
    {
      id: 'metal-stamp',
      labelKey: LOCALS.ai_identify_zone_metal_stamp,
      required: true,
      backgroundImage: constanceMetalStampImg,
    },
    {
      id: 'strap-stitch-join',
      labelKey: LOCALS.ai_identify_zone_strap_stitch_join,
      required: true,
      backgroundImage: constanceStrapStitchJoinImg,
    },
    {
      id: 'full-stamp',
      labelKey: LOCALS.ai_identify_zone_full_stamp,
      required: false,
      backgroundImage: constanceFullStampImg,
    },
    {
      id: 'magnetic-buckle',
      labelKey: LOCALS.ai_identify_zone_magnetic_buckle,
      required: false,
      backgroundImage: constanceMagneticBuckleImg,
    },
    {
      id: 'magnetic-groove',
      labelKey: LOCALS.ai_identify_zone_magnetic_groove,
      required: false,
      backgroundImage: constanceMagneticGrooveImg,
    },
  ],
};

// 获取包型配置
export const getBagTypeConfig = (
  bagType?: string,
): PhotoZoneConfig[] | undefined => {
  const normalizedType = normalizeBagType(bagType);
  if (!normalizedType) return undefined;
  return BAG_TYPE_CONFIG[normalizedType];
};

// 判断是否是支持的包型
export const isSupportedBagType = (bagType?: string): boolean => {
  const normalizedType = normalizeBagType(bagType);
  if (!normalizedType) return false;
  return SUPPORTED_BAG_TYPES.includes(
    normalizedType as (typeof SUPPORTED_BAG_TYPES)[number],
  );
};

// 一级分类中"包类"对应的 value（后端稳定业务编码，不随语言/展示名变化）
export const BAG_CATEGORY_VALUE = 'bags';

// 二级分类中 BKC 包型对应的 value 列表
export const SUPPORTED_BAG_TYPE_VALUES = ['birkin', 'kelly', 'constance'] as const;

// 根据分类 ID 获取该分类节点的 value（业务编码）
export const getCategoryValueByCategoryId = (
  categoryId: number | undefined,
  productCategoryCascaderOptions: CascaderOption[],
): string | undefined =>
  categoryId === undefined
    ? undefined
    : findCascaderOptionById(categoryId, productCategoryCascaderOptions)?.key;

// 判断所选分类是否属于包类（一级分类 value === 'bags'）
export const isBagProductCategory = (
  categoryIdList: number[] | undefined,
  productCategoryCascaderOptions: CascaderOption[],
): boolean =>
  getCategoryValueByCategoryId(
    categoryIdList?.[0],
    productCategoryCascaderOptions,
  ) === BAG_CATEGORY_VALUE;

// 判断所选分类是否属于 BKC 包型（一级分类是包类，且二级分类 value 属于 birkin/kelly/constance）
export const isBkcProductCategory = (
  categoryIdList: number[] | undefined,
  productCategoryCascaderOptions: CascaderOption[],
): boolean => {
  if (!isBagProductCategory(categoryIdList, productCategoryCascaderOptions)) {
    return false;
  }
  const secondLevelValue = getCategoryValueByCategoryId(
    categoryIdList?.[1],
    productCategoryCascaderOptions,
  );
  return (
    !!secondLevelValue &&
    (SUPPORTED_BAG_TYPE_VALUES as readonly string[]).includes(
      secondLevelValue,
    )
  );
};

// 从产品名称推导系列名
export const deriveSeriesName = (name?: string) => {
  if (!name) return undefined;
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const firstMatch = trimmed.match(/[A-Za-z]+/);
  const firstWord = firstMatch ? firstMatch[0] : trimmed.split(/\s+/)[0];
  return firstWord || undefined;
};

// 根据分类ID获取包型名称（第二级分类的 nameEn）
export const getBagTypeNameFromCategoryId = (
  categoryId: number | undefined,
  productCategoryCascaderOptions: CascaderOption[],
): string | undefined => {
  if (!categoryId) return undefined;

  // 递归查找分类路径
  const findPath = (
    options: CascaderOption[],
    targetId: number,
    path: CascaderOption[] = [],
  ): CascaderOption[] | null => {
    for (const option of options) {
      const currentPath = [...path, option];
      if (option.value === targetId) {
        return currentPath;
      }
      if (option.children) {
        const result = findPath(option.children, targetId, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  const path = findPath(productCategoryCascaderOptions, categoryId);
  if (!path || path.length < 2) return undefined;

  // 返回第二级分类的 nameEn
  return path[1]?.nameEn;
};

// 根据分类ID列表获取包型名称（用于表单模式）
export const getBagTypeNameFromCategoryIdList = (
  categoryIdList: number[] | undefined,
  productCategoryCascaderOptions: CascaderOption[],
): string | undefined => {
  if (!categoryIdList || categoryIdList.length < 2) return undefined;

  // 查找第二级分类
  const secondLevelOption = findCascaderOptionById(
    categoryIdList[1],
    productCategoryCascaderOptions,
  );
  return secondLevelOption?.nameEn;
};

// 根据分数获取鉴定结论样式
export const getVerdictStyle = (
  score: number | undefined | null,
): { backgroundColor: string; color: string } | undefined => {
  if (score === undefined || score === null) return undefined;
  // 仿品：score < 50
  if (score < 50) {
    return { backgroundColor: '#C62828', color: '#FFFFFF' };
  }
  // 疑似正品：50 ≤ score < 70
  if (score < 70) {
    return { backgroundColor: '#FFF9C4', color: '#F57F17' };
  }
  // 相对正品：70 ≤ score < 90
  if (score < 90) {
    return { backgroundColor: '#C8E6C9', color: '#2E7D32' };
  }
  // 正品：score ≥ 90
  return { backgroundColor: '#2E7D32', color: '#FFFFFF' };
};

// 根据检测点分数获取分数文字颜色
export const getPointScoreColor = (score: number): string => {
  if (score < 50) return '#C62828';
  if (score < 70) return '#F57F17';
  return '#2E7D32';
};

// bagMeta 类型
export type BagMeta = {
  brand?: string;
  names?: string;
  name?: string;
  color?: string;
  rank?: string;
  material?: string;
  hardware?: string;
  stamp?: string;
  accessory?: string;
  size?: string;
};

// 从 PMS Product 构建 bagMeta（独立模式）
export const buildBagMetaFromProduct = (
  product: {
    brandName?: string;
    name?: string;
    color?: string;
    rank?: string;
    material?: string;
    hardware?: string;
    stamp?: string;
    accessory?: string;
    size?: string;
    productCategoryId?: number;
  },
  bagTypeName?: string,
): BagMeta | undefined => {
  const names = normalizeBagType(bagTypeName) || deriveSeriesName(product.name);

  const payload: BagMeta = {
    brand: product.brandName,
    names,
    name: product.name,
    color: product.color || undefined,
    rank: product.rank || undefined,
    material: product.material || undefined,
    hardware: product.hardware || undefined,
    stamp: product.stamp || undefined,
    accessory: product.accessory || undefined,
    size: product.size || undefined,
  };

  const hasValue = Object.values(payload).some(
    (value) => value !== undefined && value !== '',
  );
  return hasValue ? payload : undefined;
};
